import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Progress,
  Segmented,
  Table,
  Tag,
  notification,
} from 'antd';
import dayjs from 'dayjs';
import { artifactApi, clientsApi } from '../api';
import { employeeId } from '../../GetRole.utils';
import {
  ARTIFACT_GROUPS,
  ARTIFACT_STATUS,
  COMPLETE_STATUSES,
} from './artifactConstants';
import ArtifactDrawer from './ArtifactDrawer';

const FILTERS = ['All', 'Needs action', 'Complete'];

const matchesFilter = (artifact, filter) => {
  if (filter === 'Needs action') {
    return artifact.isOverdue || artifact.status === 'inreview';
  }
  if (filter === 'Complete') return COMPLETE_STATUSES.includes(artifact.status);
  return true;
};

// Secondary line under the artifact name: what's been collected so far.
const artifactNote = (artifact) => {
  const notes = [];
  if (artifact.isRecurring) {
    notes.push(
      artifact.lastPeriodStart
        ? `Last report: week of ${dayjs(artifact.lastPeriodStart).format('DD MMM')}`
        : 'No reports yet'
    );
  }
  if (artifact.isPerResource) {
    notes.push(`${artifact.resourcesCovered || 0} team member(s) signed`);
  }
  if (artifact.status === 'notapplicable' && artifact.naReason) {
    notes.push(`N/A: ${artifact.naReason}`);
  }
  return notes.join(' · ');
};

const ProjectArtifacts = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [filter, setFilter] = useState('All');
  const [selectedId, setSelectedId] = useState(null);
  const [employees, setEmployees] = useState([]);

  const fetchWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const response = await artifactApi.getProjectArtifacts(projectId, employeeId);
      setWorkspace(response.data);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || 'Could not load project artifacts.',
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  useEffect(() => {
    clientsApi
      .getEmployeeName()
      .then((res) => setEmployees(res.data || []))
      .catch((err) => console.log(err));
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Artifact',
        dataIndex: 'name',
        key: 'name',
        width: '28%',
        render: (_, artifact) => (
          <Flex vertical gap={2}>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', fontWeight: 600, textAlign: 'left' }}
              onClick={() => setSelectedId(artifact.artifactId)}
            >
              {artifact.name}
            </Button>
            {artifactNote(artifact) && (
              <span style={{ fontSize: 12, color: '#5B6270' }}>
                {artifactNote(artifact)}
              </span>
            )}
          </Flex>
        ),
      },
      { title: 'Phase', dataIndex: 'phase', key: 'phase', width: '11%' },
      {
        title: 'Owner',
        dataIndex: 'ownerName',
        key: 'ownerName',
        width: '13%',
        render: (ownerName) => ownerName || <span style={{ color: '#8C95A5' }}>Unassigned</span>,
      },
      {
        title: 'Version',
        dataIndex: 'latestVersionNo',
        key: 'latestVersionNo',
        width: '8%',
        render: (versionNo) => (versionNo ? `v${versionNo}` : '-'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '13%',
        render: (status, artifact) => (
          <Flex gap={4} wrap="wrap">
            <Tag color={ARTIFACT_STATUS[status]?.color}>
              {ARTIFACT_STATUS[status]?.label || status}
            </Tag>
            {artifact.isOverdue && <Tag color="error">Overdue</Tag>}
          </Flex>
        ),
      },
      {
        title: 'Due / updated',
        key: 'dates',
        width: '12%',
        render: (_, artifact) => (
          <Flex vertical>
            {artifact.dueDate && (
              <span style={{ color: artifact.isOverdue ? '#B42318' : undefined }}>
                Due {dayjs(artifact.dueDate).format('DD/MM/YYYY')}
              </span>
            )}
            {artifact.latestUploadedAt && (
              <span style={{ fontSize: 12, color: '#5B6270' }}>
                Updated {dayjs(artifact.latestUploadedAt).format('DD/MM/YYYY')}
              </span>
            )}
            {!artifact.dueDate && !artifact.latestUploadedAt && '-'}
          </Flex>
        ),
      },
      {
        title: '',
        key: 'action',
        width: '6%',
        render: (_, artifact) => (
          <Button size="small" onClick={() => setSelectedId(artifact.artifactId)}>
            Open
          </Button>
        ),
      },
    ],
    []
  );

  const summary = workspace?.summary;
  const artifacts = workspace?.artifacts || [];
  const visible = artifacts.filter((artifact) => matchesFilter(artifact, filter));

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
        {summary ? (
          <Flex align="center" gap={16}>
            <Progress
              type="circle"
              size={64}
              percent={Math.round((summary.complete / Math.max(summary.total, 1)) * 100)}
            />
            <Flex vertical>
              <span>
                <b>{summary.complete}</b> of {summary.total} complete ·{' '}
                <b>{summary.onTrack}</b> on track
              </span>
              <span style={{ color: summary.needsAction ? '#B42318' : '#5B6270' }}>
                {summary.overdue} overdue · {summary.inReview} awaiting review
              </span>
            </Flex>
          </Flex>
        ) : (
          <span />
        )}
        <Segmented options={FILTERS} value={filter} onChange={setFilter} />
      </Flex>

      {ARTIFACT_GROUPS.map((group) => {
        const rows = visible.filter((artifact) => artifact.groupName === group.key);
        if (!loading && rows.length === 0) return null;
        return (
          <Flex vertical gap={8} key={group.key}>
            <Flex vertical>
              <h3 style={{ margin: 0 }}>{group.title}</h3>
              <span style={{ fontSize: 13, color: '#5B6270' }}>{group.description}</span>
            </Flex>
            <Table
              rowKey="artifactId"
              columns={columns}
              dataSource={rows}
              loading={loading}
              pagination={false}
              scroll={{ x: 1100 }}
              size="middle"
            />
          </Flex>
        );
      })}

      <ArtifactDrawer
        artifactId={selectedId}
        employees={employees}
        onClose={() => setSelectedId(null)}
        onChanged={fetchWorkspace}
      />
    </Flex>
  );
};

export default ProjectArtifacts;
